// The picture of any dictionary word: a root's picture (DhatuPicture), the folk-art picture of a noun, अव्यय or
// pronoun (word-images.ts), or for a number a drawn counting card (the Devanagari numeral and, up to २०, that many
// dots), so every word in the dictionary and the word pop-up can be seen as well as read.

import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { wordPicture } from '@/prakrit/lib/content/word-images'
import { NUMBERS_LIST } from '@/prakrit/lib/grammar/lexicon'
import { DhatuPicture, hasDhatuPicture } from '@/prakrit/components/common/dhatu-picture'

interface WordPictureProps {
    /** Dictionary part of speech (dhatu, noun, avyaya, pronoun, number) or analysis pos (verb, kridanta …). */
    pos: string
    /** The dictionary word or the analysis lemma. */
    word: string
    /** The word's meaning, for the alt text. */
    meaning?: string
    sizes: string
    decorative?: boolean
    priority?: boolean
    className?: string
    fallback?: ReactNode
}

const ROOT_POS = new Set(['dhatu', 'verb', 'kridanta'])

const FRAME = 'relative block aspect-square overflow-hidden rounded-2xl bg-surface-muted ring-1 ring-border'

const numberValue = (word: string): number | null => NUMBERS_LIST.find(([w]) => w === word.normalize('NFC'))?.[1] ?? null

const devanagariDigits = (value: number): string => value.toLocaleString('en-IN').replace(/[0-9]/g, d => String.fromCharCode(0x0966 + Number(d)))

export const hasWordPicture = (pos: string, word: string): boolean => {
    if (ROOT_POS.has(pos)) return hasDhatuPicture(word)
    if (pos === 'number') return numberValue(word) !== null

    return wordPicture(pos, word) !== null
}

/** A counting card: the numeral, and for 1 to 20 that many dots in rows of five. */
export const NumberCard: FC<{ value: number, label: string, decorative: boolean, className?: string }> = ({ value, label, decorative, className }): ReactNode => {
    const numeral = devanagariDigits(value)
    const dots = value <= 20 ? value : 0
    const rows = Math.ceil(dots / 5)
    const size = numeral.length <= 2 ? 40 : numeral.length <= 4 ? 30 : 20
    const textY = dots > 0 ? 44 : 60
    const startY = 62 + (4 - rows) * 4

    return (
        <span className={cn(FRAME, 'bg-saffron/8', className)}>
            <svg
                viewBox='0 0 100 100'
                role={decorative ? undefined : 'img'}
                aria-label={decorative ? undefined : label}
                aria-hidden={decorative ? true : undefined}
                className='size-full'
            >
                <text
                    x='50'
                    y={textY}
                    textAnchor='middle'
                    dominantBaseline='middle'
                    fontSize={size}
                    className='fill-saffron-strong font-heading'
                >
                    {numeral}
                </text>
                {Array.from({ length: dots }, (_, i) => {
                    const row = Math.floor(i / 5)
                    const inRow = Math.min(5, dots - row * 5)
                    const col = i % 5
                    const x = 50 + (col - (inRow - 1) / 2) * 13

                    return <circle key={i} cx={x} cy={startY + row * 11} r='4.4' className={i % 2 === 0 ? 'fill-terracotta' : 'fill-prakrit'} />
                })}
            </svg>
        </span>
    )
}

export const WordPicture: FC<WordPictureProps> = ({
    pos,
    word,
    meaning,
    sizes,
    decorative = false,
    priority = false,
    className,
    fallback = null
}): ReactNode => {
    if (ROOT_POS.has(pos)) {
        return <DhatuPicture root={word} meaning={meaning} sizes={sizes} decorative={decorative} priority={priority} className={className} fallback={fallback} />
    }

    if (pos === 'number') {
        const value = numberValue(word)

        if (value === null) return fallback

        return <NumberCard value={value} label={`${word}: ${value}`} decorative={decorative} className={className} />
    }

    const picture = wordPicture(pos, word)

    if (!picture) return fallback

    return (
        <span className={cn(FRAME, className)}>
            <Image
                src={picture.src}
                alt={decorative ? '' : `${word}${meaning ? ` (${meaning})` : ''}: ${picture.scene}`}
                width={1024}
                height={1024}
                sizes={sizes}
                priority={priority}
                className='size-full object-cover'
            />
        </span>
    )
}
